/*
 * Copyright (c) 2019
 *    Reliant Business Valuation, LLC - http://reliantvalue.com
 *    Author: Kirill Vizniuk - kirill@internalvaluation.com
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 * http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

import { NgModule } from '@angular/core';
import { Routes, RouterModule } from '@angular/router';
import {
  AccountPage, DashboardPage, ForgotPage, HomePage, LoginPage, RecoveryPage, TestPage, ProfilePage, NotificationPage,
  PreferencesPage, ExtensionPage, RegisterPage
} from './components/pages';
import { AuthenticationGuard } from './guards/auth.guard';
import { AccountNotificationsGuard } from './guards/account-notifications.guard';
import { ReportPage } from './components/pages/report/report.page';
import {
  ReportChecklistPage,
  ReportExportPage,
  ReportFilesComponent,
  ReportGeneralPage,
  ReportHistoryEmailsPage,
  ReportHistoryPage,
  ReportLinksPage,
  ReportNotesPage
} from './components/pages/report';
import { StatisticsPage } from './components/pages/statistics/statistics.page';
import { ReportEaGeneralPage, ReportEaLinksPage, ReportEaNotesPage } from './components/pages/report-ea';
import { ReportEaPage } from './components/pages/report-ea/report-ea.page';
import { MiniKanbanComponent } from './modules/ofc/components/mini-kanban/mini-kanban.component';
import { ExternalAskPage } from './components/pages/external-ask/external-ask.page';

const routes: Routes = [
  // ------------------------------
  // All NOT PROTECTED Pages goes here
  // ------------------------------

  { // Login
    path: 'login',
    component: LoginPage
  },
  { // Forgot
    path: 'forgot',
    component: ForgotPage
  },
  { // Recovery
    path: 'recovery',
    component: RecoveryPage
  },
  { // Register
    path: 'register',
    component: RegisterPage
  },
  { // External-Ask
    path: 'external/ask/:token',
    component: ExternalAskPage
  },
  // ------------------------------
  // All PROTECTED Pages goes here
  // ------------------------------
  { // Home
    path: '',
    component: HomePage,
    canActivate: [AuthenticationGuard],
    children: [
      { // Dashboard
        path: 'dashboard',
        component: DashboardPage,
        canActivate: [AuthenticationGuard],
        runGuardsAndResolvers: 'always',
      },
      { // Account
        path: 'account',
        component: AccountPage,
        canActivate: [AuthenticationGuard],
        children: [
          {
            path: 'profile',
            component: ProfilePage,
            canActivate: [AuthenticationGuard]
          },
          {
            path: 'notification',
            component: NotificationPage,
            canActivate: [AuthenticationGuard],
            canDeactivate: [AccountNotificationsGuard]
          },
          {
            path: 'preferences',
            component: PreferencesPage,
            canActivate: [AuthenticationGuard]
          },
          {
            path: '',
            redirectTo: 'profile',
            pathMatch: 'full'
          }
        ]
      },
      { // Default
        path: '',
        redirectTo: '/dashboard',
        pathMatch: 'full'
      },
      { // Report
        path: 'report/:id',
        component: ReportPage,
        canActivate: [AuthenticationGuard],
        children: [
          {
            path: 'general',
            component: ReportGeneralPage,
            canActivate: [AuthenticationGuard]
          },
          {
            path: 'checklist',
            component: ReportChecklistPage,
            canActivate: [AuthenticationGuard]
          },
          {
            path: 'history',
            component: ReportHistoryPage,
            data: { pma: true },
            canActivate: [AuthenticationGuard]
          },
          {
            path: 'links',
            component: ReportLinksPage,
            data: { pma: true },
            canActivate: [AuthenticationGuard]
          },
          {
            path: 'notes',
            component: ReportNotesPage,
            canActivate: [AuthenticationGuard]
          },
          {
            path: 'export',
            component: ReportExportPage,
            canActivate: [AuthenticationGuard]
          },
          {
            path: 'files',
            component: ReportFilesComponent,
            canActivate: [AuthenticationGuard]
          },
          {
            path: 'emails',
            component: ReportHistoryEmailsPage,
            canActivate: [AuthenticationGuard]
          },
          {
            path: '',
            redirectTo: 'general',
            pathMatch: 'full'
          }
        ]
      },
      { // Report
        path: 'report-ea/:id',
        component: ReportEaPage,
        canActivate: [AuthenticationGuard],
        children: [
          {
            path: 'general',
            component: ReportEaGeneralPage,
            canActivate: [AuthenticationGuard]
          },
          {
            path: 'history',
            component: ReportHistoryPage,
            canActivate: [AuthenticationGuard]
          },
          {
            path: 'links',
            component: ReportEaLinksPage,
            canActivate: [AuthenticationGuard]
          },
          {
            path: 'notes',
            component: ReportEaNotesPage,
            canActivate: [AuthenticationGuard]
          },
          {
            path: 'export',
            component: ReportExportPage,
            canActivate: [AuthenticationGuard]
          },
          {
            path: 'files',
            component: ReportFilesComponent,
            canActivate: [AuthenticationGuard]
          },
          {
            path: 'emails',
            component: ReportHistoryEmailsPage,
            canActivate: [AuthenticationGuard]
          },
          {
            path: '',
            redirectTo: 'general',
            pathMatch: 'full'
          }
        ]
      },
      { // Test
        path: 'test',
        component: TestPage,
        canActivate: [AuthenticationGuard]
      },
      { // Extension
        path: 'extension',
        component: ExtensionPage,
        canActivate: [AuthenticationGuard]
      },
      { // Reporting Module
        path:'reporting',
        loadChildren: ():any => import('./modules/reporting/reporting.module').then(m => m.ReportingModule),
        canActivate: [AuthenticationGuard]
      },
      { // Manage Module
        path:'manage',
        loadChildren: ():any => import('./modules/manage/manage.module').then(m => m.ManageModule),
        canActivate: [AuthenticationGuard]
      },
      { // Board Module
        path:'boards',
        loadChildren: ():any => import('./modules/board/board.module').then(m => m.BoardModule),
        canActivate: [AuthenticationGuard]
      },
      { // BVQ Module
        path:'bvq',
        loadChildren: ():any => import('./modules/bvq/bvq.module').then(m => m.BvqModule),
        canActivate: [AuthenticationGuard]
      },
      { // Quote Module
        path: 'processing',
        loadChildren: (): any => import('./modules/quote/quote.module').then(m => m.QuoteModule),
        canActivate: [AuthenticationGuard]
      },
      {
        path: 'statistics',
        component: StatisticsPage,
        canActivate: [AuthenticationGuard]
      },
      { 
        path: 'features',
        component: MiniKanbanComponent,
        canActivate: [AuthenticationGuard]
      }
    ]
  },
  { // Default
    path: '**',
    redirectTo: '',
    pathMatch: 'full'
  }
];

@NgModule({
  imports: [
    RouterModule.forRoot(routes, {
      onSameUrlNavigation: 'reload'
    })
  ],
  exports: [RouterModule],
  providers: []
})
export class AppRoutingModule { }
